import {
  ActionIcon,
  Alert,
  Anchor,
  Button,
  Group,
  Modal,
  Progress,
  ScrollArea,
  Stack,
  Stepper,
  Text,
  Title,
  Tooltip,
} from '@mantine/core'
import { useModals } from '@mantine/modals'
import { IconInfoCircle, IconTrash } from '@tabler/icons-react'
import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'

import { theme } from '@/configs/theme'
import { useAllEsks } from '@/hooks/useAllEsks'
import { useGlobalState } from '@/hooks/useGlobalState'
import { usePresentationStyles } from '@/hooks/usePresentationStyles'
import type { ResourceType } from '@/services/api/api.types'
import { type BulkUploadFile, type BulkUploadJob, REMOVABLE } from '@/services/state/BulkUploadStore'
import * as classes from '@/styles/BulkUploadWizard.css'
import { fileTypeLabel, formatFileSize } from '@/utils/bulkContentUpload'
import { getResourceIcon } from '@/utils/contentTypes'
import { deliveryModeLabel } from '@/utils/deliveryModes'
import { PSK_VALUE } from '@/utils/sidekicks'

import { confirmStop, requestRemove } from './bulkUploadConfirmations'
import { bulkUploadCopy } from './bulkUploadCopy'
import {
  bulkJobCopy,
  processingStatus,
  removeTooltip,
  retryAllLabel,
  runningNote,
  uploadingStatus,
} from './bulkUploadJobCopy'

export const UploadPanel = observer(function UploadPanel() {
  const { bulkUploadStore: store } = useGlobalState()
  // Every open Mantine modal answers Escape, so one pressed on a confirmation would also close this.
  const { modals: confirmations } = useModals()
  const job = store.job
  if (!job) return null

  // Minimize is the one way out while it runs; Escape minimizes too, since the upload carries on either way.
  return (
    <Modal
      opened={store.panelOpen}
      onClose={() => store.setPanelOpen(false)}
      closeOnClickOutside={false}
      closeOnEscape={confirmations.length === 0}
      withCloseButton={job.state !== 'running'}
      closeButtonProps={{ 'aria-label': 'Close' }}
      size="64rem"
      centered
      title={
        <Stack gap={4}>
          <Title order={3}>{bulkUploadCopy[job.mode].title}</Title>
          <Text size="sm" c="dimmed">
            {bulkJobCopy[job.mode].panel[job.state]}
          </Text>
        </Stack>
      }>
      <Stack gap="lg">
        <Stepper active={job.state === 'running' ? 2 : 3} size="sm">
          {bulkUploadCopy[job.mode].steps.map(label => (
            <Stepper.Step key={label} label={label} />
          ))}
        </Stepper>

        <OverallProgress job={job} />
        <FileRows job={job} />

        <Alert color="indigo" variant="light" icon={<IconInfoCircle />}>
          <Text size="sm">
            {job.state === 'running'
              ? runningNote
              : job.state === 'interrupted'
                ? bulkJobCopy[job.mode].panel.interruptedNote
                : bulkJobCopy[job.mode].panel.finishedNote(job)}
          </Text>
        </Alert>

        <PanelFooter job={job} />
      </Stack>
    </Modal>
  )
})

const OverallProgress = observer(function OverallProgress({ job }: { job: BulkUploadJob }) {
  const clean = job.failed === 0
  const status =
    job.state === 'running'
      ? `${job.percent}%`
      : job.state === 'interrupted'
        ? 'Interrupted'
        : clean
          ? 'All done'
          : 'Finished with errors'

  return (
    <Stack gap={6}>
      <Group justify="space-between">
        <Text size="sm" fw={500}>
          {bulkJobCopy[job.mode].panel.progress(job)}
          {job.failed > 0 && (
            <Text span inherit c="red">
              {` · ${job.failed} failed`}
            </Text>
          )}
        </Text>
        <Text size="sm" fw={500}>
          {status}
        </Text>
      </Group>
      <Progress
        value={job.state === 'running' ? job.percent : 100}
        striped={job.state === 'running'}
        animated={job.state === 'running'}
        color={job.state === 'running' ? 'indigo' : job.state === 'interrupted' ? 'yellow' : clean ? 'green' : 'red'}
        aria-label="Overall upload progress"
      />
    </Stack>
  )
})

const FileRows = observer(function FileRows({ job }: { job: BulkUploadJob }) {
  const { getEskName } = useAllEsks()
  const stylesQuery = usePresentationStyles()
  const modeLabels = useMemo(
    () => new Map((stylesQuery.data ?? []).map(s => [s.name, deliveryModeLabel(s.name, s.displayName || s.name)])),
    [stylesQuery.data],
  )

  const describe = (file: BulkUploadFile) => {
    const parts = [file.name, fileTypeLabel(file.name), formatFileSize(file.size)]
    if (job.mode === 'modules') {
      parts.push(file.eskId === PSK_VALUE ? 'Personal Sidekick' : (getEskName(file.eskId) ?? 'Sidekick'))
      if (file.deliveryModeName) parts.push(modeLabels.get(file.deliveryModeName) ?? file.deliveryModeName)
    }
    return parts.join(' · ')
  }

  return (
    <ScrollArea.Autosize mah={theme.rem(360)}>
      <Stack gap={6}>
        {job.files.map(file => (
          <FileRow key={file.name} job={job} file={file} meta={describe(file)} />
        ))}
      </Stack>
    </ScrollArea.Autosize>
  )
})

const FileRow = observer(function FileRow({
  job,
  file,
  meta,
}: {
  job: BulkUploadJob
  file: BulkUploadFile
  meta: string
}) {
  const { bulkUploadStore: store } = useGlobalState()
  const Icon = getResourceIcon(file.fileType as ResourceType)
  const removable = job.state === 'running' && REMOVABLE.includes(file.status)

  return (
    <Group className={classes.fileRow} wrap="nowrap" gap="sm">
      <Icon size={20} stroke={1.5} aria-hidden="true" className={classes.fileIcon} />
      <Stack gap={4} className={classes.fileText}>
        <Text size="sm" fw={500} truncate="end">
          {file.title}
        </Text>
        <Text size="xs" c="dimmed" truncate="end">
          {meta}
        </Text>
        <Progress
          size="sm"
          value={rowProgress(file)}
          color={file.status === 'failed' ? 'red' : file.status === 'complete' ? 'green' : 'indigo'}
          striped={file.status === 'processing'}
          animated={file.status === 'processing'}
          aria-label={`${file.title} progress`}
        />
      </Stack>
      <Group gap="xs" wrap="nowrap" className={classes.rowStatus}>
        <RowStatus job={job} file={file} />
        {removable && (
          <Tooltip label={removeTooltip}>
            <ActionIcon
              variant="subtle"
              color="gray"
              aria-label={`Remove ${file.title}`}
              onClick={() => requestRemove(store, file.name)}>
              <IconTrash size={16} aria-hidden="true" />
            </ActionIcon>
          </Tooltip>
        )}
      </Group>
    </Group>
  )
})

/**
 * One bar for the whole file, as the overall figure counts it: the upload fills the first half and
 * processing the second, so it only ever moves forward.
 */
const rowProgress = (file: BulkUploadFile) => {
  switch (file.status) {
    case 'uploading':
      return file.progress / 2
    case 'processing':
      return 50 + (file.processed ?? 0) / 2
    case 'queued':
    case 'interrupted':
      return 0
    default:
      return 100
  }
}

const STATUS_TEXT: Record<Exclude<BulkUploadFile['status'], 'uploading' | 'failed'>, string> = {
  queued: 'Queued',
  processing: 'Processing',
  complete: 'Complete',
  interrupted: "Didn't upload",
}

const RowStatus = observer(function RowStatus({ job, file }: { job: BulkUploadJob; file: BulkUploadFile }) {
  const { bulkUploadStore: store } = useGlobalState()
  if (file.status === 'uploading') return <Text size="sm">{uploadingStatus(file.progress)}</Text>
  if (file.status === 'processing' && file.processed !== null) {
    return <Text size="sm">{processingStatus(file.processed)}</Text>
  }
  if (file.status !== 'failed') return <Text size="sm">{STATUS_TEXT[file.status]}</Text>
  // Nothing is left in memory to retry once the page has closed.
  if (job.state === 'interrupted') {
    return (
      <Text size="sm" c="red">
        Failed
      </Text>
    )
  }
  return (
    <Anchor
      component="button"
      type="button"
      size="sm"
      c="red"
      aria-label={retryAllLabel(job.failed)}
      onClick={() => void store.retryFailed()}>
      Retry
    </Anchor>
  )
})

const PanelFooter = observer(function PanelFooter({ job }: { job: BulkUploadJob }) {
  const { bulkUploadStore: store } = useGlobalState()

  if (job.state === 'running') {
    return (
      <Group justify="flex-end" gap="md">
        {/* The panel opens focused here, not on the first file's Remove, which takes a queued file without asking. */}
        <Button variant="default" data-autofocus onClick={() => store.setPanelOpen(false)}>
          Minimize
        </Button>
        {job.waiting > 0 && (
          <Button variant="outline" color="red" onClick={() => confirmStop(store)}>
            Stop Upload
          </Button>
        )}
      </Group>
    )
  }

  if (job.state === 'interrupted') {
    return (
      <Group justify="flex-end">
        <Button onClick={() => store.dismiss()}>Dismiss</Button>
      </Group>
    )
  }

  return (
    <Group justify="flex-end" gap="md">
      {job.failed > 0 && (
        <Button variant="default" onClick={() => void store.retryFailed()}>
          Retry {job.failed} Failed
        </Button>
      )}
      <Button onClick={() => store.done()}>Done</Button>
    </Group>
  )
})
